export type AuthMode = 'login' | 'signup';

// Auth screens, and account deletion, are never a place to come back to.
// A delete URL remembered from the previous account must not open for the next one.
const NOT_A_RETURN_PATH = /^\/(login|signup|register|account\/delete)(?=[/?#]|$)/;

/**
 * An in-app path that is safe to send someone back to after they sign in.
 * Returns '' for anything else, so a crafted `?redirect=` cannot leave the site.
 */
export const safeReturnPath = (value: string | null | undefined): string => {
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return '';
    return NOT_A_RETURN_PATH.test(value) ? '' : value;
};

/** Link to the login or signup screen that brings the visitor back to `returnTo` afterwards. */
export const authHref = (mode: AuthMode, returnTo?: string | null): string => {
    const target = safeReturnPath(returnTo);
    return target && target !== '/' ? `/${mode}?redirect=${encodeURIComponent(target)}` : `/${mode}`;
};

// Same key the protected-route redirect already uses, so one path means one thing.
const RETURN_PATH_KEY = 'postLoginRedirectPath';

/**
 * Remember where a brand-new account should land after onboarding.
 * Session storage only: it survives the OAuth round trip in this tab and is gone when the tab closes.
 */
export const rememberReturnPath = (path: string): void => {
    const target = safeReturnPath(path);
    if (target) sessionStorage.setItem(RETURN_PATH_KEY, target);
};

export const readReturnPath = (): string => safeReturnPath(sessionStorage.getItem(RETURN_PATH_KEY));

export const clearReturnPath = (): void => {
    sessionStorage.removeItem(RETURN_PATH_KEY);
    localStorage.removeItem(RETURN_PATH_KEY);
};
